import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Calendar,
  ArrowLeft,
  ExternalLink,
  MapPin,
  Info,
  Flag,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Map from '../components/Map';
import { Button } from '../components/ui/button';
import { useRace } from '../hooks/useProfile';
import { dayjs } from '../lib/utils';
import Loader from './Loader';

const RaceDetail = () => {
  const { id } = useParams({ from: '/races/$id' });
  const navigate = useNavigate();
  const { data: race, isLoading } = useRace(id);

  if (isLoading) {
    return <Loader />;
  }

  if (!race) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="text-gray-500">Race not found</div>
        <Button variant="outline" onClick={() => navigate({ to: '/races' })}>
          Back to Races
        </Button>
      </div>
    );
  }

  const raceDate = dayjs(race.date);
  const daysUntilRace = raceDate.diff(dayjs(), 'days');

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-1 pb-6 md:px-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate({ to: '/races' })}
        className="hidden items-center gap-1 md:flex"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Races
      </Button>

      <div className="surface-card overflow-hidden bg-brand-midnight p-6 sm:p-8">
        <p className="eyebrow text-brand-volt">Target race</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-white">
          {race.name}
        </h1>
        <p className="mt-2 flex items-center gap-1.5 text-white/70">
          <Calendar className="h-4 w-4" />
          {raceDate.format('dddd, MMMM D, YYYY')}
          <span className="text-brand-volt font-semibold">
            {daysUntilRace > 0
              ? `(${daysUntilRace} days away)`
              : daysUntilRace === 0
                ? '(Today!)'
                : '(Past race)'}
          </span>
        </p>
      </div>

      <div className="surface-card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:p-6">
        <div className="text-center">
          <div className="text-xs text-brand-slate mb-1 uppercase tracking-wide">
            Distance
          </div>
          <div className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-midnight px-4 py-2 text-sm font-bold text-white uppercase tracking-wide">
            <Flag className="h-4 w-4 text-brand-green" />
            {race.distance || '—'}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-brand-slate mb-1 uppercase tracking-wide">
            Website
          </div>
          {race.url ? (
            <a
              href={race.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 text-lg font-semibold text-brand-cobalt hover:text-brand-midnight"
            >
              Open
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : (
            <div className="text-lg font-semibold text-brand-midnight">—</div>
          )}
        </div>
      </div>

      <div className="surface-card p-5 sm:p-6">
        <h3 className="text-sm font-semibold text-brand-midnight mb-3 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Route Overview
        </h3>
        {race.gpx_file_url ? (
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <Map url={race.gpx_file_url} />
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-lg bg-brand-offWhite border border-brand-green/20 p-4 text-sm text-brand-slate">
            <MapPin className="h-5 w-5 shrink-0 text-brand-green" />
            <p>Route map will appear here when GPX data is added.</p>
          </div>
        )}
      </div>

      <div className="surface-card p-5 sm:p-6">
        <h3 className="text-sm font-semibold text-brand-midnight mb-2 flex items-center gap-2">
          <Info className="h-4 w-4" />
          Race information to know about
        </h3>
        {race.description ? (
          <div className="prose prose-sm max-w-none text-brand-slate">
            <ReactMarkdown>{race.description}</ReactMarkdown>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-lg bg-brand-offWhite border border-brand-volt/15 p-4 text-sm text-brand-slate">
            <Info className="h-5 w-5 shrink-0 text-brand-volt" />
            <p>
              Important race logistics, packet pickup, start-line notes, aid
              stations, parking, and course-specific guidance will be shown here
              when seeded.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaceDetail;
